import React, { useReducer, useState, useEffect, useMemo } from "react";
import { Button } from "../../../../../design_system/components/Button/Button.jsx";
import CustomCalendarPicker from "../../../../cta_utils/CalendarPopup";
import { Popup } from "../../../../../design_system/components/Popup/Popup.jsx";
import { dateFormator } from "../../../../utils/dateUtils";
import { DateInputBox, DateSeparator } from "../../../../input_utils/DateInputBox";
import { PresetOption } from "../../../aggregates/components/PresetOption";
import { SMART_PRESET_MAPPING } from "../../../../utils/presetUtil";
import { Icons } from "../../../../../design_system/components/Icons";
import { Tooltip } from "../../../../../design_system/components/ToolTip/ToolTip";
import { TabItem } from "../../../../../design_system/components/Tabs/TabItem.jsx";
import { TabGroup } from "../../../../../design_system/components/Tabs/TabGroup.jsx";
import { IconButton } from "../../../../../design_system/components/Button/Button.jsx";
import { Tracker } from "../../../../utils/mixpanel/tracker.js";

const MatchingArticleDateFilters = ({
  filtersState,
  filtersStateReducer,
  tempFilterReducer,
  customStyle = { position: "relative", color: "var(--color-text-primary)" },
  innerText = "Date Range",
  initialTab = "created_at",
  position = "center",
  theme,
  smartPublishData_team_info = {},
  maxPopUpWidth,
  trackAnalytics = false,
  tab,
}) => {
  const [tempFiltersState, dispatchTemp] = useReducer(tempFilterReducer, { ...filtersState });
  const [popup, setPopup] = useState({ isOpen: false, activeTab: initialTab, errors: {} });
  const [calendarStartDate, setCalendarStartDate] = useState(new Date());
  const togglePopup = e => { if (e) e.stopPropagation(); setPopup(prev => ({ ...prev, isOpen: !prev.isOpen })); };
  const setActiveTab = tab => setPopup(prev => ({ ...prev, activeTab: tab }));

  const getPresetsToShow = () => {
    const tab = popup.activeTab;
    const presetType = tempFiltersState.dateRanges?.[tab]?.presetType || "date_ranges";
    return SMART_PRESET_MAPPING[presetType] || [];
  };

  useEffect(() => {
    if (popup.isOpen) {
      const activeKey = popup.activeTab;
      const activeRange = filtersState.dateRanges?.[activeKey];
      const fallbackPreset = { label: "All time", key: "all_time", value: {} };
      dispatchTemp({
        type: "SET_PRESET",
        payload: {
          tab: activeKey,
          preset: activeRange?.preset || fallbackPreset,
          value: activeRange?.value || fallbackPreset.value,
        },
      });
      dispatchTemp({ type: "APPLY_DATE_RANGES", payload: { dateRanges: filtersState.dateRanges } });
      setPopup(prev => ({ ...prev, errors: {} }));
    }
  }, [popup.isOpen, filtersState.dateRanges]);

  const dateRangeApplied = useMemo(() => {
    return Object.entries(filtersState.dateRanges || {}).some(([key, range]) => {
      const defaultKey = range?.defaultPresetKey;
      const currentKey = range?.preset?.key || range?.defaultPresetKey;
      return currentKey && defaultKey && currentKey !== defaultKey;
    });
  }, [filtersState.dateRanges]);

  const onSelectPreset = (preset) => {
    const { value } = preset;
    if (value.from) setCalendarStartDate(new Date(value.from.getFullYear(), value.from.getMonth(), 1));
    dispatchTemp({ type: "SET_PRESET", payload: { tab: popup.activeTab, preset, value: { from: value.from, to: value.to } } });
    setPopup(prev => ({ ...prev, errors: {} }));
  };

  const dateChangeCallback = (val) => {
    dispatchTemp({ type: "UPDATE_DATE_RANGE", payload: { key: popup.activeTab, subkey: "value", value: val } });
    dispatchTemp({ type: "SET_PRESET", payload: { tab: popup.activeTab, preset: { label: "Custom range", key: "custom", value: val }, value: val } });
    setPopup(prev => ({ ...prev, errors: {} }));
  };

  const onClearAll = () => {
    dispatchTemp({ type: "CLEAR_DATE_RANGES" });
    setCalendarStartDate(new Date());
  };

  const applyRanges = () => {
    const activeTab = popup.activeTab;
    const currentValue = tempFiltersState.dateRanges?.[activeTab]?.value || {};
    if ((currentValue.from && !currentValue.to) || (!currentValue.from && currentValue.to)) {
      setPopup(prev => ({ ...prev, errors: { apply: "Select the full date range" } }));
      return;
    }
    if (trackAnalytics) {
      const activeRange = tempFiltersState.dateRanges?.[activeTab];
      const isCustom = (activeRange?.preset?.key || activeRange?.defaultPresetKey) === "custom";
      Tracker.track("Newsfeed Date Filter Applied", { tab, date_type: activeRange?.label, range_value: isCustom ? "custom" : (activeRange?.preset?.label || activeRange?.defaultPresetKey || "all_time"), });
    }
    filtersStateReducer({ type: "APPLY_DATE_RANGES", payload: { dateRanges: tempFiltersState.dateRanges } });
    togglePopup();
  };

  const renderPopupContent = () => {
    const dateRanges = tempFiltersState.dateRanges || {};
    const tabsKeys = Object.keys(dateRanges);
    if (!tabsKeys.length) return null;
    const activeKey = tabsKeys.includes(popup.activeTab) ? popup.activeTab : tabsKeys[0];
    const rangeObj = dateRanges[activeKey] || { value: {}, maxDate: new Date(), preset: "All time" };
    const { from, to } = rangeObj.value;
    const maxDate = rangeObj.maxDate;
    return (
      <div onClick={e => e.stopPropagation()} className="flex date-range-popup__content">
        <div className="popup-col date-range-popup__left-col">
          <TabGroup variant="line" fullWidth>
            {tabsKeys.map(key => {
              const isActive = activeKey === key;
              const hasChangedPreset = dateRanges[key].defaultPresetKey !== (tempFiltersState.dateRanges[key]?.preset?.key || tempFiltersState.dateRanges[key]?.defaultPresetKey);
              return (
                <TabItem
                  key={key}
                  label={dateRanges[key].label}
                  isActive={isActive}
                  onClick={() => setActiveTab(key)}
                  style={hasChangedPreset && !isActive ? { color: 'var(--color-action-primary)' } : {}}
                />
              );
            })}
          </TabGroup>
          <div className="popup-scroll-section flex flex-col gap-m date-range-popup__presets">
            {getPresetsToShow().map(preset => (
              <PresetOption
                key={preset.key}
                preset={preset}
                isActive={ preset.key === (tempFiltersState.dateRanges?.[popup.activeTab]?.preset?.key || tempFiltersState.dateRanges?.[popup.activeTab]?.defaultPresetKey) }
                onSelectPreset={onSelectPreset}
                smartPublishData={smartPublishData_team_info}
              />
            ))}
          </div>
        </div>
        <div className="date-range-popup__right-col">
          <div className="flex items-center date-range-popup__date-inputs">
            <DateInputBox side="from" from={from} to={to} />
            <DateSeparator />
            <DateInputBox side="to" from={from} to={to} />
          </div>
          <div className="date-range-popup__calendar">
            <CustomCalendarPicker
              to={to}
              from={from}
              maxDate={maxDate}
              calendarStartDate={calendarStartDate}
              setCalendarStartDate={setCalendarStartDate}
              selectRange={true}
              handleDateChange={dateChangeCallback}
            />
          </div>
          {popup.errors.apply && (
            <div className="body-s-semibold date-range-popup__error">
              {popup.errors.apply}
            </div>
          )}
        </div>
      </div>
    );
  };

  const Trigger = () =>
    theme === "GREEN" ? (
      <Tooltip
        tooltipText={() => "Date Range"}
        placement="bottom"
        showWithoutOverflowing
      >
        <IconButton
          Size="Small"
          Type="Tertiary"
          IconName="date"
          onClick={togglePopup}
          IsSelected={dateRangeApplied}
        />
      </Tooltip>
    ) : (
      <div
        className="filters-button-box body-l-bold"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", ...customStyle }}
        onClick={togglePopup}
      >
        <Icons name="date" size={22} className="date-filter-dropdown__icon" />
        {innerText && <div className="date-filter-dropdown__text">{innerText}</div>}
      </div>
    );

  return (
    <>
      <Trigger />
      {popup.isOpen && (
        <Popup
          name="Date Range"
          handleOnClosePopup={togglePopup}
          maxWidth={maxPopUpWidth}
          customContentStyle={{ padding: 0 }}
          primaryAction={{ label: "Apply", onClick: applyRanges }}
          secondaryAction={{ label: "Cancel", onClick: togglePopup }}
          extraAction={{ label: "Clear", onClick: onClearAll, style: { color: "var(--color-link-default)" } }}
          extraActionClassName="body-s-bold link-default"
        >
          {renderPopupContent()}
        </Popup>
      )}
    </>
  );
};

export default MatchingArticleDateFilters;
